import type { ReactNode } from 'react'

import type { MedicalIssue, ProcedureSearchRow } from 'features/Schedule/api/medicalIssues.types'

import type { PickerEntry, PickerKey } from './issuePicker.types'
import {
  PICKER_TEXT,
  formatCodeChip,
  formatIssueUSDate,
  getEmptyProblemsMessage,
  getPickerKey,
  problemEntry,
  procedureEntry,
} from './issuePicker.utils'
import styles from './medicalissues.module.scss'

// antd option, leaf or group (rc-select uses one recursive type); `entry` is what a pick hands back.
export type PickerOption = {
  value?: PickerKey
  label: ReactNode
  entry?: PickerEntry | null
  disabled?: boolean
  options?: PickerOption[]
}

type BuildOptionsArgs = {
  problems: MedicalIssue[]
  // null = the procedure search failed; the problems half is still drawn.
  procedures: ProcedureSearchRow[] | null
  showProcedures: boolean
}

const PROCEDURES_FAILED_KEY: PickerKey = 'procedure:0'

// One line, code | name, like the UPC and encounter selects; the sub line is the OE detail.
const OptionRow = ({ title, sub, code }: { title: string; sub?: string; code?: string }) => (
  <div className={styles.option}>
    {code && <span className={styles.optionCode}>{code}</span>}
    {code && <span className={styles.optionSeparator}>|</span>}
    <span className={styles.optionName}>{title}</span>
    {sub && <span className={styles.optionSub}>{sub}</span>}
  </div>
)

const getProblemSub = (issue: MedicalIssue): string => {
  const parts = [issue.type_label || issue.type]
  const lastSeen = formatIssueUSDate(issue.last_seen)
  if (lastSeen) parts.push(`${PICKER_TEXT.lastSeen} ${lastSeen}`)
  return parts.filter(Boolean).join(' - ')
}

const problemOption = (issue: MedicalIssue): PickerOption => ({
  value: getPickerKey({ kind: 'problem', id: issue.id }),
  label: (
    <OptionRow
      title={issue.title}
      sub={getProblemSub(issue)}
      code={formatCodeChip((issue.diagnosis ?? '').split(';')[0])}
    />
  ),
  entry: problemEntry(issue),
})

const procedureOption = (row: ProcedureSearchRow): PickerOption => ({
  value: getPickerKey({ kind: 'procedure', id: row.code_id }),
  label: <OptionRow title={row.label} code={formatCodeChip(row.code)} />,
  entry: procedureEntry(row),
})

const proceduresFailedOption: PickerOption = {
  value: PROCEDURES_FAILED_KEY,
  label: <span className={styles.optionNote}>{PICKER_TEXT.proceduresFailed}</span>,
  entry: null,
  disabled: true,
}

// The patient's own problems come first, because that is what the visit is usually for.
export const buildPickerOptions = ({
  problems,
  procedures,
  showProcedures,
}: BuildOptionsArgs): PickerOption[] => {
  const groups: PickerOption[] = []
  if (problems.length) {
    groups.push({ label: PICKER_TEXT.problemsGroup, options: problems.map(problemOption) })
  }
  if (showProcedures && procedures === null) {
    groups.push({ label: PICKER_TEXT.proceduresGroup, options: [proceduresFailedOption] })
  } else if (showProcedures && procedures?.length) {
    groups.push({ label: PICKER_TEXT.proceduresGroup, options: procedures.map(procedureOption) })
  }
  return groups
}

// What the empty popup says; an empty box must say why it is empty.
export const getNotFoundMessage = (term: string, hasRecordedProblems: boolean): string =>
  term.trim() === '' ? getEmptyProblemsMessage(hasRecordedProblems) : PICKER_TEXT.nothingMatches
